import { CheckCircleFilled, CloseOutlined, PlusCircleFilled, WarningFilled } from '@ant-design/icons'
import { useOrderMovements } from '@api/useOrderMovements'
import { GraviButton, Texto } from '@gravitate-js/excalibrr'
import { Input, Select, Tooltip } from 'antd'
import { Fragment, useMemo, useState } from 'react'

import { SingleError } from './SingleError'
import { useSecurityContext } from '@components/Navigation/SecurityContext'

export function ErrorControl({ order, gridAPIRef }) {
  const [isApproving, setIsApproving] = useState(false)

  return isApproving ? (
    <ErrorApproval gridAPIRef={gridAPIRef} order={order} setIsApproving={setIsApproving} />
  ) : (
    <ErrorDisplay setIsApproving={setIsApproving} order={order} />
  )
}

function ErrorApproval({ order, gridAPIRef, setIsApproving }) {
  const [reason, setReason] = useState('')
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [reasonNote, setReasonNote] = useState()
  const [addingNote, setAddingNote] = useState()
  const { useMovementErrorReasonCodes, approveMovementErrorMutation } = useOrderMovements()
  const { data: reasonCodes } = useMovementErrorReasonCodes()
  const { mutateAsync: approveErrors } = approveMovementErrorMutation()
  const approveOrder = () => {
    approveErrors({ order_id: order.id, reason, note: reasonNote || '' }).then(() => gridAPIRef?.current?.redrawRows())
  }

  const options = useMemo(() => {
    return reasonCodes?.map((r) => ({ value: r }))
  }, [reasonCodes])

  return (
    <div className='p-3 flex items-center validation-error-display'>
      <Texto category='h5' weight='bold' appearance='error' className='mr-5'>
        <CheckCircleFilled className='mr-3' /> Error Approval
      </Texto>
      {addingNote ? (
        <>
          <div className='flex-1 vertical-flex-center mr-2'>
            <Input
              onChange={(e) => setReasonNote(e.target.value)}
              style={{ width: '100%' }}
              placeholder='Enter Bypass Note'
            />
          </div>
          <div className='mr-2'>
            <Tooltip title='Cancel Adding Note'>
              <GraviButton
                onClick={() => {
                  setAddingNote(false)
                  setReasonNote(undefined)
                }}
                icon={<CloseOutlined />}
              />
            </Tooltip>
          </div>
        </>
      ) : (
        <>
          <div className='flex-1 vertical-flex-center'>
            <Texto appearance='error' weight='bold'>
              Are You Sure?
            </Texto>
            <Texto weight={600} appearance='error' className='error-approval-confirmation'>
              The errors on this order are calling out a critical issue. You can also fix these errors by fixing and
              saving the Order Movement. Are you sure you want to proceed?{' '}
            </Texto>
          </div>
          <div className='mr-4'>
            <GraviButton onClick={() => setAddingNote(true)} buttonText='Add Note' icon={<PlusCircleFilled />} />
          </div>
        </>
      )}

      <Select
        style={{ minWidth: 200 }}
        onChange={(value) => {
          setReason(value)
          setDropdownOpen(false)
        }}
        value={reason}
        placeholder='Select a Reason'
        options={options}
        loading={!reasonCodes}
        open={dropdownOpen}
        onDropdownVisibleChange={() => setDropdownOpen(!dropdownOpen)}
        onBlur={() => setDropdownOpen(false)}
        onFocus={() => setDropdownOpen(true)}
      />
      <div className='mx-2'>
        <GraviButton buttonText='Cancel' onClick={() => setIsApproving(false)} />
      </div>
      <GraviButton buttonText='Approve Errors' disabled={!reason} onClick={approveOrder} error className='mx-3' />
    </div>
  )
}

function ErrorDisplay({ order, setIsApproving }) {
  const { scopes } = useSecurityContext()
  const canApprove = scopes?.scopes?.includes('om.w')
  return (
    <div className='p-3 flex items-center validation-error-display'>
      <Texto category='h6' appearance='error' weight='bold' className='mr-4'>
        <WarningFilled className='mr-3' /> Order Movement Errors
      </Texto>
      <div style={{ flexWrap: 'wrap' }} className='flex-1 flex items-center'>
        {order.movement_validation_issues.map((e, i) => (
          <Fragment key={i}>
            <SingleError error={e} key={i} />
            {i !== order.movement_validation_issues.length - 1 && (
              <div
                className='mx-3 flex justify-center'
                style={{ width: 1, height: 12, background: 'var(--theme-error)' }}
              />
            )}
          </Fragment>
        ))}
      </div>
      {canApprove && (
        <GraviButton buttonText='Approve Errors' error className='mx-3' onClick={() => setIsApproving(true)} />
      )}
    </div>
  )
}
